"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const liquid_tap_1 = require("liquid-tap");
const md5_1 = __importDefault(require("md5"));
const client_events_1 = require("./const/client-events");
const enum_1 = require("./const/enum");
const common_1 = require("./utils/common");
const resolveIdentity = (liquidTap) => {
    if (liquidTap.identity !== undefined && liquidTap.identity !== null) {
        return liquidTap.identity;
    }
    return common_1.randStr(10);
};
const generateRequestId = (identity, requestObj, entropy) => `req-${md5_1.default(JSON.stringify(identity) + JSON.stringify(requestObj) + (entropy || 0).toString())}`;
const generateOrderId = (identity) => `ord-${md5_1.default(JSON.stringify(identity) + common_1.randStr(10))}`;
const defaultOptions = {
    lqdHost: "qed.liquid.com",
    lqdPort: 443,
    lqdTls: true,
    lqdUrl: "/rfq/trade_on_quote",
    headers: {},
    trace: null,
    auth: null,
    httpRequest: common_1.httpRequest,
};
class QexClient {
    constructor(liquidTap, options) {
        this.liquidTap = liquidTap;
        this.config = Object.assign(defaultOptions, options);
        if (this.config.auth === null && this.liquidTap.options.auth !== null) {
            this.config.auth = this.liquidTap.options.auth;
        }
    }
    quoteStreamFromHelper(helper, sessionId) {
        return this.quoteStream({
            symbol: helper.symbol(),
            currency: helper.currency(),
            side: helper.side(),
            quantity: helper.quantity(),
            sessionId,
        });
    }
    quoteStreamFromHelperDeprecated(helper) {
        return this.quoteStream({
            symbol: helper.symbol(),
            currency: helper.currency(),
            side: helper.side(),
            quantity: helper.quantity(),
        });
    }
    /**
     * Request a quote stream form the QEX service
     *
     * @param {string} symbol
     * @param {string} currency
     * @param {string} side see SIDE enum
     * @param {float} quantity
     * @param {integer} duration (Optional) Duration to stream tick for.
     * If not set, QEX will stream until the websocket disconnects
     *
     * @returns {LiquidTap.Channel}
     *
     * Channel will tick `updated` events:
  
      Quote {
          // unique id as assigned by the Initiator (uniqueness is not enforced though)
          string request_id;
  
          // globally unique server id
          bytes server_id;
  
          // globally unique quote id
          bytes quote_id;
  
          // CCY1/CCY2
          string symbol;
  
          // The currency in which the quoted quantity is denominated
          string currency;
  
          // The requested side
          string side;
  
          // Implied based on requested quantity.
          // Maximum auto-quotable quantity may be less than requested.
          // Possibly zero, if not permitted.
          // JSON value will be a number or one of the special
          // string values "NaN", "Infinity", and "-Infinity".
          // Either numbers or strings are accepted. Exponent notation is also accepted.
          double quoted_quantity;
  
          // JSON value will be a number or one of the special
          // string values "NaN", "Infinity", and "-Infinity".
          // Either numbers or strings are accepted. Exponent notation is also accepted.
          double dealable_quantity;
  
          // The requested amount has the market depth to be dealable
          bool quantity_accepted;
  
          // precision
          int32 rate_precision;
  
          // formatting rate for display purposes
          int32 pip_size;
  
          // formatting rate for display purposes
          int32 big_figure;
  
          // supplied for indicative purposes only
          Rate client_mid;
  
          Rate client_bid;
  
          Rate client_ask;
  
          Precision ccy1_volume_precision;
  
          Precision ccy2_volume_precision;
  
          // Validity window of this quote after subsequent quote
          // emitted by server (server will add grace period)
          int32 hold_time_ms;
  
          // Optional - preferred. Refer to ISO 8601:2004 e.g. yyyy-mm-ddThh:mm:ss[.mmm]Z
          iso8601_utc_timestamp quote_datetime;
      }
     */
    quoteStream(o) {
        let quantityRaw = o.quantity !== undefined && o.quantity !== ""
            ? o.quantity
            : undefined;
        // if (o.entropy !== undefined && quantityRaw !== undefined){
        //   let quantityNum = Number(quantityRaw);
        //   for(let i = 0; i < o.entropy; i ++) {
        //     quantityNum = addMicroDust(quantityNum)
        //   }
        //   quantityRaw = quantityNum;
        // }
        const request = {
            request_type: enum_1.PRODUCT_TYPE.FX_SPOT,
            /**
             * CCY1/CCY2
             * (Must include '/' character to separate the two currencies)
             * @var {string} symbol
             */
            symbol: o.symbol,
            /**
             * Currency in which quantity is denominated (one of CCY1 or CCY2)
             * @var {string} currency
             */
            currency: o.currency,
            /**
             * Optional. Indicate buy or sell base currency (AKA CCY1)
             * from client perspective (or both if absent)
             * Use string representation of QuoteRequestSide. Will default to BOTH sides if omitted.
             * @var {string} side
             */
            side: o.side,
            /**
             * Quantity of currency. If absent an indicative quote will be supplied.
             * JSON value will be a number or one of the special string values
             * "NaN", "Infinity", and "-Infinity".
             * Either numbers or strings are accepted. Exponent notation is also accepted.
             * @var {float} quantity
             */
            quantity: quantityRaw !== undefined ? quantityRaw.toString() : undefined,
            /**
             * Preferred time to hold quote
             * The server will continue to stream
             * @var {integer} preferred_hold_period_msec
             */
            preferred_hold_period_msec: o.holdPeriod,
            /**
             * Specified elapsed time after which the quote server will stop ticking.
             * Zero will continue to stream unless unsubscribed (TODO)
             * @var {integer} streaming_duration_secs
             */
            streaming_duration_secs: o.duration,
            session_id: o.sessionId,
        };
        const requestId = generateRequestId(resolveIdentity(this.liquidTap), request, o.entropy);
        request.request_id = requestId;
        if (o.partnerKey !== undefined) {
            request.api_key = o.partnerKey;
        }
        else if (this.config.partnerKey !== undefined) {
            request.api_key = this.config.partnerKey;
        }
        if (o.fundingMethod !== undefined) {
            request.funding_settlement = {
                method: o.fundingMethod,
            };
        }
        if (o.payoutMethod !== undefined) {
            request.payout_settlement = {
                method: o.payoutMethod,
            };
        }
        const channelName = request.api_key !== undefined
            ? client_events_1.QUOTE_STREAM_B2B_CHANNEL(requestId)
            : client_events_1.QUOTE_STREAM_CHANNEL(requestId);
        if (this.config.trace instanceof Function) {
            this.config.trace("REQUEST_CHANNEL", channelName);
            this.config.trace("QUOTE_REQUEST", JSON.stringify(request, null, 2));
        }
        const channel = o.delaySubscription
            ? this.liquidTap.getChannel(channelName, true, request)
            : this.liquidTap.subscribe(channelName, request);
        channel.reference = requestId;
        if (this.config.trace instanceof Function) {
            channel.bind("updated", (quote) => {
                this.config.trace("QUOTE", quote);
            });
        }
        return channel;
    }
    /**
     * Execute a deal based on a quote
     *
     * @param {object} quote Quote object returned by the quote stream
     * @param {string} side Deal side. Either 'buy' or 'sell'.
     * @param {string} requestId (Optional) Used for logging and to improve fallback validation
     * @param {object|null} account {type: "USER_ID"|"VENDOR_ACCOUNT_ID", id: <string|integer>}
     * @param {string|null} openingUserId
     * @returns {Promise}
     * Promise should resolve with:
      ExecutionReport {
          // id as assigned by the Initiator (uniqueness is not enforced though)
          string request_id;
  
          //
          string client_order_id;
  
          // Globally unique trade identifier. Supplied if trade was successful.
          string id;
  
          //
          string quantity;
  
          //
          string price;
  
          // "buy" or "sell"
          string taker_side;
  
          int32 created_at;
  
          // May explain error reason
          repeated string rejected_reason;
      }
     */
    tradeOnQuote(quote, side, requestId, account, openingUserId) {
        if (!this.config.auth) {
            return Promise.reject(new Error("No authentication specified. Must specify `auth` to execute a deal."));
        }
        /**
         * price and option_2 are mutually optional,
         * however when supplying both, the deal server will perform an extra sanity check on the deal
         */
        const dlSide = side !== undefined ? side.toLowerCase() : enum_1.SIDE.SELL;
        const price = quote.client_ask !== undefined
            ? quote.client_ask.value
            : quote.client_bid !== undefined
                ? quote.client_bid.value
                : null;
        const order = {
            /**
             * Client Order Id
             * unique id for this order, to be set by client.
             * @var {string} client_order_id
             */
            client_order_id: generateOrderId(resolveIdentity(this.liquidTap)),
            /**
             * Order Type
             * Previously Quoted
             * @var {string} order_type
             *
             */
            order_type: enum_1.PRODUCT_TYPE.FX_SPOT,
            /**
             * Quote ID
             * Supplied when dealing on previous quote. May become optional in future.
             * @var {string} quote_id
             */
            quote_id: quote.quote_id,
            /**
             * Symbol
             * CCY1/CCY2
             * @var {string} symbol
             */
            symbol: quote.symbol.toUpperCase(),
            /**
             * Currency
             * Traded currency, CCY1 or CCY2
             * @var {string} currency
             */
            currency: quote.currency.toUpperCase(),
            /**
             * Side
             * @var {string} side
             */
            side: dlSide,
            /**
             * Quantity
             * Quantity of traded currency.
             * May be rejected if quantity does not fall within quoted volume band.
             * @var {string} quantity
             */
            quantity: quote.dealable_quantity.toString(),
            /**
             * Quantity
             * Quantity of contra currency.
             * May be rejected if quantity does not fall within quoted volume band.
             * @var {string} quantity_2
             */
            quantity_2: quote.quoted_quantity.toString(),
            /**
             * Price
             * Price for previously quoted or maximum slippage for at-market deals
             * @var {string} price
             */
            price: price !== null && price !== undefined ? price.toString() : undefined,
            /**
             * Price Range
             * Future. Optional slippage tolerance for order_type of at_best_with_range
             * @var {string} price_range
             */
            price_range: null,
            /**
             * Account Type
             * Account identifier type USER_ID or VENDOR_ACCOUNT_ID [optional]
             * @var {string} account_type
             */
            account_type: account ? account.type : undefined,
            /**
             * Account
             * Account identifier value [optional]
             * @var {string} account
             */
            account: account ? account.id : undefined,
            /**
             * Opening User Id
             * user initiating the order [optional]
             * @var {string} opening_user_id
             */
            opening_user_id: openingUserId,
            /**
             * RequestID (Optional)
             * The ID used when opening the original quote request
             * Used for logging serverside
             */
            request_id: requestId,
        };
        if (this.config.trace instanceof Function) {
            this.config.trace("NEW_ORDER_SINGLE", JSON.stringify(order, null, 2));
        }
        return this.sendOrderRequest(order);
    }
    /**
     * Send Order Request
     * @param {object} quote Streamed Quote
     * @param {object} order Prepared Order}
     *
     * @returns {Promise}
     */
    sendOrderRequest(order) {
        return new Promise((resolve, reject) => {
            liquid_tap_1.resolveAuth(this.config.auth, this.config.lqdUrl)((jwt) => this._transmitOrderRequest(order, jwt)
                .then((httpResponse) => {
                const executionReport = JSON.parse(httpResponse.data);
                resolve(executionReport);
            })
                .catch(reject));
        });
    }
    _transmitOrderRequest(order, jwt) {
        const options = {
            hostname: this.config.lqdHost,
            port: this.config.lqdPort,
            path: this.config.lqdUrl,
            method: "POST",
            data: order,
            useTls: this.config.lqdTls,
            headers: Object.assign(this.config.headers, {
                "Content-Type": "application/json",
                "X-Quoine-Auth": jwt,
                "Accept-Encoding": "identity",
            }),
        };
        if (this.config.trace instanceof Function) {
            this.config.trace("HTTP_REQUEST_OPTIONS", JSON.stringify(options, null, 2));
        }
        return this.config.httpRequest(options);
    }
}
exports.QexClient = QexClient;
